import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { LibraryNavigation } from "../components/navigation/LibraryNavigation";
import { toast } from "sonner";
import { 
  Sparkles, Wand2, ArrowLeft, Send, Image, Loader2, Download, Star
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

const AIMessages = () => {
  const { token } = useAuth();
  const [receivedMessages, setReceivedMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);

  useEffect(() => { fetchMessages(); }, []);

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`${API}/ai-messages/received`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReceivedMessages(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050810] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050810]" data-testid="ai-messages-page">
      <header className="fixed top-0 left-0 right-0 z-50 glass-strong">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2">
            <img src="/logo.png" alt="StardomFans" className="w-10 h-10 object-contain" style={{ mixBlendMode: "lighten" }} />
          </Link>
          <Link to="/dashboard">
            <Button className="bg-[#0A0E18] border border-[#1A2030] text-white hover:bg-[#1A2030]" data-testid="back-dashboard-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Dashboard
            </Button>
          </Link>
        </div>
      </header>

      <main className="pt-24 pb-20 px-6">
        <LibraryNavigation />
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="font-['Unbounded'] text-3xl font-bold text-white mb-2" data-testid="ai-msg-title">
              <Wand2 className="w-8 h-8 inline mr-3 text-[#EC008C]" />
              My AI Messages
            </h1>
            <p className="text-[#8A92A0]">Personalized messages and custom AI-generated scenes from your favorite artists</p>
          </div>

          {/* Selected Message Full View */}
          {selectedMessage && (
            <div className="card p-8 mb-8 relative overflow-hidden" data-testid="ai-msg-fullview">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#D14A85] via-[#EC008C] to-[#FF6BC5]" />
              <button onClick={() => setSelectedMessage(null)} className="text-[#8A92A0] hover:text-white text-sm mb-4">&larr; Back to all messages</button>
              
              <div className="text-center mb-6">
                <span className="text-xs text-[#EC008C] uppercase tracking-widest">A Personal Message From</span>
                <h2 className="font-['Unbounded'] text-2xl font-bold text-white mt-1">{selectedMessage.artist_name}</h2>
                <p className="text-sm text-[#8A92A0] mt-1">To: {selectedMessage.fan_name}</p>
              </div>
              
              {selectedMessage.scene_image_url && (
                <div className="rounded-xl overflow-hidden mb-6 max-w-lg mx-auto">
                  <img 
                    src={selectedMessage.scene_image_url} 
                    alt="Custom AI Scene" 
                    className="w-full object-cover"
                    data-testid="ai-scene-image"
                  />
                </div>
              )}
              
              <div className="max-w-xl mx-auto bg-[#0A0E18] rounded-xl p-6 border border-[#1A2030]">
                <p className="text-white text-lg leading-relaxed italic">"{selectedMessage.personalized_text}"</p>
                <p className="text-right text-[#EC008C] font-medium mt-4">— {selectedMessage.artist_name}</p>
              </div>
              
              <p className="text-center text-xs text-[#4A5260] mt-4">
                Generated on {new Date(selectedMessage.created_at).toLocaleDateString()}
              </p>
            </div>
          )}

          {!selectedMessage && (
            <>
              {receivedMessages.length === 0 ? (
                <div className="card p-16 text-center">
                  <Wand2 className="w-16 h-16 text-[#8A92A0] mx-auto mb-4" />
                  <h2 className="font-['Unbounded'] text-xl font-bold text-white mb-2">No AI messages yet</h2>
                  <p className="text-[#8A92A0] mb-6">Visit an artist's profile to get a personalized AI message with a custom scene!</p>
                  <Link to="/fandom">
                    <Button className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399]" data-testid="browse-artists-for-ai-btn">
                      Explore Artists
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {receivedMessages.map((msg) => (
                    <button
                      key={msg.message_id}
                      onClick={() => setSelectedMessage(msg)}
                      className="card p-5 text-left hover:border-[#EC008C]/30 transition-all group"
                      data-testid={`ai-msg-${msg.message_id}`}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#D14A85]/20 to-[#EC008C]/20 flex items-center justify-center">
                          <Wand2 className="w-5 h-5 text-[#EC008C]" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-white group-hover:text-[#EC008C] transition-colors">{msg.artist_name}</h3>
                          <p className="text-xs text-[#8A92A0]">To: {msg.fan_name}</p>
                        </div>
                      </div>
                      {msg.scene_image_url && (
                        <div className="rounded-lg overflow-hidden mb-3 h-32">
                          <img src={msg.scene_image_url} alt="" className="w-full h-full object-cover" />
                        </div>
                      )}
                      <p className="text-sm text-[#8A92A0] line-clamp-2 italic">"{msg.personalized_text}"</p>
                      <p className="text-xs text-[#4A5260] mt-2">{new Date(msg.created_at).toLocaleDateString()}</p>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default AIMessages;
